import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useContext } from 'react';
import { AppContext } from '../context/AppContext';
import { api } from '../api';

const emptyForm = {
  name: '',
  parent_id: null,
};

function flattenSuites(suites, level = 0, excludeId = null) {
  const result = [];
  suites.forEach((suite) => {
    if (suite.id !== excludeId) {
      result.push({ ...suite, level });
      if (suite.children) {
        result.push(...flattenSuites(suite.children, level + 1, excludeId));
      }
    }
  });
  return result;
}

export default function SuiteForm() {
  const { projectId, suiteId } = useParams();
  const navigate = useNavigate();
  const { refresh } = useContext(AppContext);

  const [form, setForm] = useState(emptyForm);
  const [initialForm, setInitialForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [loadedSuite, setLoadedSuite] = useState(null);
  const [allSuites, setAllSuites] = useState([]);
  const dirtyRef = useRef(false);

  const isDirty = JSON.stringify(form) !== JSON.stringify(initialForm);
  useEffect(() => { dirtyRef.current = isDirty; }, [isDirty]);

  // Block browser tab close/refresh when dirty
  useEffect(() => {
    const handler = (e) => { if (dirtyRef.current) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // Load suite
  useEffect(() => {
    if (suiteId) {
      api.getSuite(Number(suiteId)).then((suite) => {
        setLoadedSuite(suite);
        const f = { name: suite.name, parent_id: suite.parent_id };
        setForm(f);
        setInitialForm(f);
      });
    }
  }, [suiteId]);

  // Load all suites (for parent selector)
  useEffect(() => {
    if (projectId) {
      api.getSuites(Number(projectId)).then((suites) => {
        const flattened = flattenSuites(suites, 0, Number(suiteId));
        setAllSuites(flattened);
      });
    }
  }, [projectId, suiteId]);

  const backUrl = `/projects/${projectId}/suites/${suiteId}`;
  const set = (field, val) => setForm({ ...form, [field]: val });

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateSuite(Number(suiteId), form);
      dirtyRef.current = false;
      refresh();
      navigate(backUrl);
    } catch (err) {
      alert('Save failed: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this suite?')) return;
    try {
      await api.deleteSuite(Number(suiteId));
      dirtyRef.current = false;
      refresh();
      navigate(`/projects/${projectId}`);
    } catch (err) {
      alert('Delete failed: ' + err.message);
    }
  };

  return (
    <div className="tc-form fullscreen-form">
      <div className="form-toolbar">
        <button className="btn-secondary" onClick={() => {
          if (dirtyRef.current && !window.confirm('You have unsaved changes. Discard them?')) return;
          navigate(backUrl);
        }} disabled={saving}>
          ← Back
        </button>
        <h3>Edit Suite</h3>
        <div className="form-toolbar-actions">
          <button className="btn-primary" onClick={handleSave} disabled={!isDirty || saving}>
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button className="btn-danger" onClick={handleDelete}>Delete</button>
        </div>
      </div>

      <div className="form-body">
        <div className="form-main">
          <label>Name</label>
          <input type="text" value={form.name} onChange={(e) => set('name', e.target.value)} />

          <label>Parent Suite</label>
          <select value={form.parent_id || ''} onChange={(e) => set('parent_id', e.target.value ? Number(e.target.value) : null)}>
            <option value="">Root (no parent)</option>
            {allSuites.map((s) => (
              <option key={s.id} value={s.id}>
                {'━ '.repeat(s.level)}{s.name}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}
